import { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { type Heartbeat } from '../../common/types';
import { useHeartbeatsService } from '../../services/heartbeats';
import { heartbeatsResource } from '../../services/resources';

export const useSwitchController = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: heartbeatsData, update } = useResource(heartbeatsResource);
	const { isSwitchLoading, switchHeartbeat } = useHeartbeatsService();

	const [switchingItem, setSwitchingItem] = useState<Heartbeat>();
	const [isSwitchModalOpen, setSwitchModalOpen] = useState(false);

	const handleModalClose = useCallback(() => {
		setSwitchModalOpen(false);
		setSwitchingItem(undefined);
	}, []);

	assert(
		!!heartbeatsData,
		new AssertionError('useSwitchController', 'heartbeats data should not be undefined!'),
	);

	const handleSwitch = useCallback(
		(heartbeat?: Heartbeat) => {
			assert(
				!!(switchingItem || heartbeat),
				new AssertionError('useSwitchController', 'switching item should not be undefined!'),
			);

			const enable = switchingItem?.status === 'Off' || heartbeat!.status === 'Off';

			return switchHeartbeat(enable, switchingItem?.name || heartbeat!.name)
				.then((res) => {
					const newData = { ...heartbeatsData };
					newData.heartbeats!.find((heartbeat, index) => {
						if (heartbeat.name === res?.name) {
							newData.heartbeats![index] = res;
						}
					});
					update(() => newData);

					handleModalClose();
				})
				.then(() => {
					const event = createAnalyticsEvent({});
					if (enable) {
						fireTrackAnalytics(event, 'heartbeatTurnedOn succeeded');
					} else {
						fireTrackAnalytics(event, 'heartbeatTurnedOff succeeded');
					}
				})
				.catch(() => {
					const event = createAnalyticsEvent({});
					fireTrackAnalytics(event, 'heartbeatSwitch failed');
				});
		},
		[
			switchingItem,
			switchHeartbeat,
			heartbeatsData,
			update,
			handleModalClose,
			createAnalyticsEvent,
		],
	);

	const handleSwitchClick = useCallback(
		(heartbeat: Heartbeat) => {
			if (heartbeat.status === 'Off') {
				handleSwitch(heartbeat);
			} else {
				setSwitchingItem(heartbeat);
				setSwitchModalOpen(true);
			}
		},
		[handleSwitch],
	);

	return {
		switchingItem,
		isSwitchModalOpen,
		isSwitchLoading,
		handleSwitchClick,
		handleModalClose,
		handleSwitch,
	};
};
